/**
 * HERO CATEGORY CARDS - API TO UI DATA TRANSFORMER
 * ================================================
 * 
 * Purpose:
 * Transforms nested category API data into Amazon-style hero discovery cards
 * Displays first 4 top-level categories, each showing exactly 4 subcategories
 * 
 * -------------------
 * DATA RELATIONSHIPS
 * -------------------
 * 
 * API Structure (FetchNestedCategories):
 * 
 * Category (Top-level)
 * ├── categoryId       → Used for card ID
 * ├── name            → Card title
 * ├── slug            → URL routing (/category/:slug)
 * ├── description     → Not used in cards
 * └── subCategories[] → Array of subcategories
 *     ├── subCategoryId  → Item ID
 *     ├── name          → Item label
 *     ├── slug          → URL routing (/subcategory/:slug)
 *     ├── imageUrl      → Item image (primary)
 *     └── imageUrls[]   → Fallback images
 * 
 * -------------------
 * ROUTING PATTERNS
 * -------------------
 * 
 * 1. Subcategory Click (item.link):
 *    /subcategory/:subCategorySlug
 *    → Routes to SubCategoryPage component
 *    → Shows all products in that subcategory
 * 
 * 2. "See all" Click (category.categoryLink):
 *    /category/:categorySlug
 *    → Routes to CategoryPage component
 *    → Shows all subcategories and their products
 * 
 * -------------------
 * UI CARD STRUCTURE
 * -------------------
 * 
 * Output Format:
 * {
 *   id: categoryId,
 *   title: category name,
 *   categoryLink: /category/:slug,
 *   items: [
 *     {
 *       id: subCategoryId,
 *       label: subcategory name,
 *       image: imageUrl,
 *       link: /subcategory/:slug
 *     },
 *     ... (exactly 4 items)
 *   ]
 * }
 * 
 * -------------------
 * BUSINESS RULES
 * -------------------
 * 
 * ✓ Show only first 4 categories (Amazon pattern)
 * ✓ Each category must have ≥4 subcategories (filter requirement)
 * ✓ Show exactly 4 subcategories per card (2x2 grid)
 * ✓ Use slug for routing when available, fallback to ID
 * ✓ Handle missing images gracefully (placeholder fallback)
 * 
 */

export const transformCategoriesToHeroCards = (apiCategories) => {
  if (!apiCategories || !Array.isArray(apiCategories)) {
    console.warn('Invalid categories data provided to transformer');
    return [];
  }

  // Filter categories that have at least 4 subcategories
  const categoriesWithEnoughSubs = apiCategories.filter(
    cat => cat.subCategories && cat.subCategories.length >= 4
  );

  // Take first 4 categories only (Amazon-style)
  const topCategories = categoriesWithEnoughSubs.slice(0, 4);

  return topCategories.map((category) => {
    // Take first 4 subcategories for each category
    const topSubCategories = category.subCategories.slice(0, 4);

    return {
      id: category.categoryId,
      title: category.name,
      // Route to category page to see all subcategories
      categoryLink: `/category/${category.slug || category.categoryId}`,
      items: topSubCategories.map((subCat) => ({
        id: subCat.subCategoryId,
        label: subCat.name,
        // Use subcategory image if available, fallback to placeholder
        image: subCat.imageUrl || 
               subCat.imageUrls?.[0] || 
               '/api/placeholder/300/300',
        // Route to subcategory page
        link: `/subcategory/${subCat.slug || subCat.subCategoryId}`,
      }))
    };
  });
};

/**
 * Fallback placeholder if API fails or returns insufficient data
 */
export const getPlaceholderHeroCards = () => {
  return [
    {
      id: 'placeholder-1',
      title: 'Loading Categories...',
      categoryLink: '#',
      items: Array(4).fill(null).map((_, i) => ({
        id: `placeholder-item-${i}`,
        label: 'Loading...',
        image: '/api/placeholder/300/300',
        link: '#'
      }))
    }
  ];
};
